import * as React from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GroupsIcon from "@mui/icons-material/Groups";
import ParticipantCard from "./ParticipantCard";
import { getLeagueParticipantsRoster } from "../services/api/leagues";

/**
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   leagueId: string
 *   leagueName: string
 */
export default function ParticipantListDialog({ open, onClose, leagueId, leagueName }) {
  const [participants, setParticipants] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open || !leagueId) return;
    setLoading(true);
    setError("");
    getLeagueParticipantsRoster(leagueId)
      .then((data) => setParticipants(Array.isArray(data) ? data : []))
      .catch(() => setError("Failed to load participants. Please try again."))
      .finally(() => setLoading(false));
  }, [open, leagueId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <GroupsIcon />
          <span>
            Participants
            {leagueName ? ` — ${leagueName}` : ""}
          </span>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && participants.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            No participants have signed up yet.
          </Typography>
        )}

        {!loading && participants.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {participants.length} unique participant{participants.length !== 1 ? "s" : ""}
            </Typography>
            {participants.map((p, i) => (
              <React.Fragment key={p.userId}>
                <ParticipantCard participant={p} />
                {i < participants.length - 1 && <Divider sx={{ my: 0.5 }} />}
              </React.Fragment>
            ))}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
